import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Braces, Cable, Check, ChevronDown, Filter, KeyRound, Search } from 'lucide-react';
import { fetchIntegrations, getCachedIntegrations, type IntegrationListItem } from '../lib/catalog';
import { IntegrationLogo } from '../components/IntegrationLogo';
import { SectionIntro } from '../components/alt/SectionIntro';
const PAGE_SIZE = 24;
const METHOD_OPTIONS = ['API', 'MCP', 'OAuth'] as const;
type IntegrationMethod = typeof METHOD_OPTIONS[number];
const METHOD_ICONS = {
  API: Braces,
  MCP: Cable,
  OAuth: KeyRound
} as const;
function getIntegrationMethods(integration: IntegrationListItem): Set<IntegrationMethod> {
  const methods = new Set<IntegrationMethod>();
  const row = integration as IntegrationListItem & Record<string, unknown>;
  const fields = ['method', 'methods', 'recommended_method', 'recommendedMethod', 'api', 'mcp', 'oauth', 'auth_type', 'auth_types', 'authType', 'authTypes', 'authentication', 'capabilities'];
  const addValue = (value: unknown, hint?: string) => {
    if (hint && value !== false && value != null && ['api', 'mcp', 'oauth', 'oauth2', 'rest', 'graphql'].includes(hint.toLowerCase())) {
      addValue(hint);
    }
    if (typeof value === 'string') {
      const tokens = value.toLowerCase().split(/[^a-z0-9]+/);
      if (tokens.includes('api') || tokens.includes('rest') || tokens.includes('graphql')) methods.add('API');
      if (tokens.includes('mcp')) methods.add('MCP');
      if (tokens.includes('oauth') || tokens.includes('oauth2')) methods.add('OAuth');
    } else if (Array.isArray(value)) {
      value.forEach(item => addValue(item));
    } else if (value && typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
        if (['key', 'name', 'type', 'method', 'auth_type', 'authType'].includes(key)) addValue(item);else if (['api', 'mcp', 'oauth', 'oauth2', 'rest', 'graphql'].includes(key.toLowerCase())) addValue(item, key);
      });
    }
  };
  fields.forEach(field => addValue(row[field], field));
  return methods;
}

/**
 * The mark now falls through the catalog URL, Simple Icons and Dashboard Icons
 * before it ever shows a letter — see components/IntegrationLogo. The previous
 * version dropped straight to an initial whenever the catalog had no logo,
 * which is why whole pages of the directory read as coloured letter tiles.
 */
function LogoOrInitial({
  item
}: {
  item: IntegrationListItem;
}) {
  return <IntegrationLogo slug={item.slug} name={item.display_name} logoUrl={item.logo_url} className="size-9" data-id="element-10620" data-mp="3h2slak1zf6if" />;
}
export function Integrations() {
  // Seed from the build-time prime cache when present (SSR/prerender), so the
  // static HTML renders the full list. In the browser the cache is empty →
  // falls through to the fetch below (unchanged behaviour).
  const primed = getCachedIntegrations();
  const [integrations, setIntegrations] = useState<IntegrationListItem[]>(primed ?? []);
  const [loading, setLoading] = useState(!primed);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedMethods, setSelectedMethods] = useState<IntegrationMethod[]>([]);
  const [methodsExpanded, setMethodsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchIntegrations().then(rows => {
      if (!cancelled) {
        setIntegrations(rows);
        setError(null);
      }
    }).catch(e => {
      if (!cancelled) setError(e.message || 'Failed to load integrations');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    integrations.forEach(i => i.categories.forEach(c => counts.set(c.name, (counts.get(c.name) ?? 0) + 1)));
    return counts;
  }, [integrations]);
  const categories = useMemo(() => [...categoryCounts.keys()].sort(), [categoryCounts]);
  const filtered = useMemo(() => {
    return integrations.filter(i => {
      if (selectedCategory && !i.categories.some(c => c.name === selectedCategory)) return false;
      if (selectedMethods.length > 0) {
        const methods = getIntegrationMethods(i);
        if (methods.size > 0 && !selectedMethods.some(method => methods.has(method))) return false;
      }
      if (query) {
        const q = query.toLowerCase();
        return i.display_name.toLowerCase().includes(q) || i.slug.toLowerCase().includes(q) || i.categories.some(c => c.name.toLowerCase().includes(q));
      }
      return true;
    });
  }, [integrations, query, selectedCategory, selectedMethods]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const rangeStart = filtered.length === 0 ? 0 : pageStart + 1;
  const rangeEnd = Math.min(pageStart + PAGE_SIZE, filtered.length);
  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedCategory, selectedMethods]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  const goToPage = (nextPage: number) => {
    setCurrentPage(Math.max(1, Math.min(nextPage, totalPages)));
  };
  const toggleMethod = (method: IntegrationMethod) => {
    setSelectedMethods(selected => selected.includes(method) ? selected.filter(item => item !== method) : [...selected, method]);
  };
  return <div className="min-h-screen bg-[#FAF8F4] px-6 pb-24 pt-32" data-id="element-10621" data-mp="1tmdrxcb2k044">
      <div className="mx-auto max-w-6xl" data-id="element-10622" data-mp="3rym5kmhivecr">
        {/* Left-aligned, in the home page's display treatment: the search field
         and the filter rail beneath are both left-anchored, so a centred
         heading gave the page two axes. The count is stated in the lead
         because "how many" is the first question a directory raises. */}
        <SectionIntro align="left" className="mb-10 max-w-3xl" data-id="element-10623" data-mp="1q47ys6oivzgt">
          <h1 className="font-title text-[40px] font-bold leading-[1.04] tracking-[-0.03em] text-[#2B211A] sm:text-[52px]" data-id="element-10624" data-mp="1ccjkpxupr0x7">
            Integrations
          </h1>
          <p className="mt-5 max-w-[56ch] text-[16.5px] leading-[1.7] text-[#6B5D52] md:text-[17.5px]" data-id="element-10625" data-mp="241mmn6z4nz6o">
            Giga connects with the tools you already use. Search for an
            integration or browse by category below.
          </p>
        </SectionIntro>

        {/* Search */}
        <div className="mb-8" data-id="element-10626" data-mp="200azr97n1s26">
          <div className="relative max-w-lg" data-id="element-10627" data-mp="1zz87awuo3hk5">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-[18px] -translate-y-1/2 text-[#A0917F]" data-id="element-10628" data-mp="202du25ftq7cp" />
            <input type="text" placeholder="Search integrations…" value={query} onChange={e => setQuery(e.target.value)} className="w-full rounded-full border border-[#E7E0D4] bg-white py-3.5 pl-12 pr-4 text-[15px] text-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.04)] transition-[border-color,box-shadow] duration-200 ease-out placeholder:text-[#A0917F] focus:border-[#EA580C] focus:outline-none focus:ring-2 focus:ring-[#EA580C]/25" data-id="element-10629" data-mp="olxdg8sj9ko9" />
          </div>
        </div>

        {/* Results */}
        {loading ? <div data-loading="true" className="text-center py-20 text-neutral-400 text-lg" data-id="element-10630" data-mp="3tiqor7ioi8fx">
            Loading integrations…
          </div> : error ? <div className="text-center py-20" data-id="element-10631" data-mp="3olrver6ua5is">
            <p className="text-neutral-500 text-lg" data-id="element-10632" data-mp="73fdzxsa8ri4">
              Couldn't load integrations right now.
            </p>
            <p className="text-neutral-400 text-sm mt-2" data-id="element-10633" data-mp="2cp5ibok88j7">{error}</p>
          </div> : integrations.length === 0 ? <div className="text-center py-20" data-id="element-10634" data-mp="3arqrzk4xzuc3">
            <p className="text-neutral-400 text-lg" data-id="element-10635" data-mp="3aqpj9mbefjxj">No integrations available.</p>
          </div> : <div className="grid grid-cols-1 min-[960px]:grid-cols-4 gap-4 md:gap-6" data-id="element-10636" data-mp="35tfl9i9y1zy3">
            <div className="min-[960px]:col-span-1 flex flex-col gap-2 min-[960px]:max-h-[600px] min-[960px]:overflow-y-auto min-[960px]:pr-2" data-id="element-10637" data-mp="2kbxj5lw894qa">
              <div className="rounded-lg border border-sand-200 text-[13px] text-ink" data-id="element-10638" data-mp="2u8tstuxv3wwf">
                <button type="button" onClick={() => setMethodsExpanded(expanded => !expanded)} aria-expanded={methodsExpanded} aria-controls="integration-method-filters" className="flex w-full items-center gap-2 rounded-lg py-2 px-3 text-left font-medium transition-colors hover:border-sand-300" data-id="element-10639" data-mp="2uawpgxtisoa4">
                  <span className="flex-1" data-id="element-10640" data-mp="385yyhttnytr5">Filters</span>
                  {selectedMethods.length > 0 && <span className="max-w-24 truncate text-xs font-semibold text-brand" data-id="element-10641" data-mp="3d14ob3q6sski">{selectedMethods.join(', ')}</span>}
                  <ChevronDown className={`h-4 w-4 flex-none text-ink-faint transition-transform ${methodsExpanded ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-10642" data-mp="3mxhis3tk3agb" />
                </button>
                {methodsExpanded && <div id="integration-method-filters" className="space-y-2 border-t border-sand-200 px-3 py-2.5" data-id="element-10643" data-mp="1fb7r5u9u2alm">
                  {METHOD_OPTIONS.map(method => {
                const MethodIcon = METHOD_ICONS[method];
                const iconColor = method === 'API' ? 'text-brand' : method === 'MCP' ? 'text-emerald-600' : 'text-amber-600';
                return <label key={method} className="group -mx-1.5 flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 font-medium transition-colors hover:bg-sand-100/70 focus-within:bg-sand-100/70" data-id="element-10644" data-mp="11d9fiwmwcs9t">
                      <span className="relative flex h-4 w-4 flex-none items-center justify-center" data-id="element-10645" data-mp="1u15ey93uoyaa">
                        <input type="checkbox" checked={selectedMethods.includes(method)} onChange={() => toggleMethod(method)} className="peer h-4 w-4 cursor-pointer appearance-none rounded-[5px] border border-[#EAE5DD] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.04)] transition-all hover:border-[#D9D1C7] checked:border-brand checked:bg-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25 focus-visible:ring-offset-1" data-id="element-10646" data-mp="1p7khqja308c5" />
                        <Check className="pointer-events-none absolute h-3 w-3 text-white opacity-0 transition-opacity peer-checked:opacity-100" strokeWidth={3} aria-hidden="true" data-id="element-10647" data-mp="rauc550kjwgz" />
                      </span>
                      <MethodIcon className={`h-4 w-4 flex-none ${iconColor}`} aria-hidden="true" data-id="element-10648" data-mp="1188rf1araf7p" />
                      <span data-id="element-10649" data-mp="1f7i1rkca6f3r">{method}</span>
                    </label>;
              })}
                </div>}
              </div>
              <button type="button" onClick={() => setSelectedCategory(null)} aria-pressed={!selectedCategory} className={`inline-flex items-center gap-2 rounded-lg border py-2 px-3 text-left text-[13px] transition-colors ${!selectedCategory ? 'border-brand text-brand font-semibold' : 'border-sand-200 text-ink font-medium hover:border-sand-300'}`} data-id="element-10650" data-mp="1a8neo67a64du">
                <Filter className={`w-4 h-4 flex-none ${!selectedCategory ? '' : 'text-ink-faint'}`} data-id="element-10651" data-mp="1txpjtyydlw3x" />
                <span className="flex-1" data-id="element-10652" data-mp="2ckyfb11gt6uv">All Categories</span>
                <span className="tabular-nums text-ink-faint" aria-label={`${integrations.length} integrations`} data-id="element-10653" data-mp="2cjdljdmz5ry2">{integrations.length}</span>
              </button>
              {categories.map(cat => <button key={cat} type="button" onClick={() => setSelectedCategory(cat)} aria-pressed={selectedCategory === cat} className={`inline-flex items-center gap-2 rounded-lg border py-2 px-3 text-left text-[13px] transition-colors ${selectedCategory === cat ? 'border-brand text-brand font-semibold' : 'border-sand-200 text-ink font-medium hover:border-sand-300'}`} data-id="element-10654" data-mp="23kiddw0de7s4">
                  <span className={`w-1.5 h-1.5 rounded-full flex-none ${selectedCategory === cat ? 'bg-brand' : 'bg-sand-300'}`} data-id="element-10655" data-mp="1kvlfzj9oez5o" />
                  <span className="flex-1" data-id="element-10656" data-mp="1ott0mp79gkm5">{cat}</span>
                  <span className="tabular-nums text-ink-faint" aria-label={`${categoryCounts.get(cat)} integrations`} data-id="element-10657" data-mp="1ax5o3e2trlv4">{categoryCounts.get(cat)}</span>
                </button>)}
            </div>
            <div className="min-[960px]:col-span-3 min-w-0" data-id="element-10658" data-mp="3hkmt4fbjqydl">
              <p className="text-sm text-neutral-500 mb-3" aria-live="polite" data-id="element-10659" data-mp="3hm7m40i7q99c">
                Showing {rangeStart}–{rangeEnd} of {filtered.length} integrations
              </p>
              {filtered.length === 0 ? <div className="text-center py-20" data-id="element-10660" data-mp="33sykim0ias1d">
                <p className="text-neutral-400 text-lg" data-id="element-10661" data-mp="33u9ksbuqthnq">No integrations found.</p>
                <button type="button" onClick={() => {
              setQuery('');
              setSelectedCategory(null);
              setSelectedMethods([]);
            }} className="mt-4 text-sm text-[#EA580C] hover:underline" data-id="element-10662" data-mp="4e4jkgpw0hva">
                  Clear filters
                </button>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 min-[960px]:grid-cols-3 gap-3 content-start" data-id="element-10663" data-mp="3mk96cordkeme">
              {pageItems.map(integration => <Link key={integration.slug} to={`/integrations/${integration.slug}`} className="group flex min-w-0 items-center gap-3 rounded-2xl border border-[#E7E0D4] bg-white p-3.5 shadow-[0_1px_2px_rgba(43,33,26,0.04)] transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-px hover:border-[#D9CFBE] hover:shadow-[0_1px_2px_rgba(43,33,26,0.05),0_14px_30px_-22px_rgba(43,33,26,0.5)]" data-id="element-10664" data-mp="2g4p4k0pz4yi5">
                  <LogoOrInitial item={integration} data-id="element-10665" data-mp="2fpa2cqvkhmp5" />
                  <span className="flex min-w-0 flex-col text-left" data-id="element-10666" data-mp="2fq1xy1p5zkff">
                    <span className="truncate text-[14px] font-semibold text-[#2B211A] transition-colors group-hover:text-[#EA580C]" data-id="element-10667" data-mp="2jrdgjn9mf5e4">
                      {integration.display_name}
                    </span>
                    {integration.categories[0] && <span className="truncate text-[11px] text-[#8A7B6D]" data-id="element-10668" data-mp="2tpktzw4pb5bd">
                        {integration.categories[0].name}
                      </span>}
                  </span>
                </Link>)}
              </div>}
              {totalPages > 1 && <nav className="mt-6 flex items-center justify-center gap-3" aria-label="Integration results pagination" data-id="element-10669" data-mp="149tqpmysfkxf">
                <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 1} className="rounded-lg border border-sand-200 bg-white px-3 py-2 text-sm font-medium text-ink transition-colors hover:border-sand-300 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-10670" data-mp="2uqfi4v3x519b">
                  Previous
                </button>
                <span className="text-sm text-neutral-500" aria-current="page" data-id="element-10671" data-mp="2ytlo01n4cqf9">Page {page} of {totalPages}</span>
                <button type="button" onClick={() => goToPage(page + 1)} disabled={page === totalPages} className="rounded-lg border border-sand-200 bg-white px-3 py-2 text-sm font-medium text-ink transition-colors hover:border-sand-300 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-10672" data-mp="2b1573uf4b4hj">
                  Next
                </button>
              </nav>}
            </div>
          </div>}
      </div>
    </div>;
}